import * as React from 'react';

import { replaceElement } from '@lendi-jam/generic-rich-text';
import { embedMarketingTrackingIntoURL } from '@lendi-jam/jam-link';
import TextLayout, { type LayoutButtonProps } from '@lendi-jam/text-layout';
import { useCommPanel } from '@lendi/navbar';
import parse from 'html-react-parser';

import { PrismicLink, TextFromRichText } from '#contentTypes/PrismicTypes';
import { TextLayoutContent } from '#contentTypes/TextLayout';


import { getButton } from '../lib/prismicFunctions';



interface PrismicTextLayoutProps {
  component: TextLayoutContent;
  layoutVariant: 'one' | 'two';
}

const PrismicTextLayout: React.FC<PrismicTextLayoutProps> = ({ component, layoutVariant }) => {
  const { openChat } = useCommPanel();
  const onClickChat = (e: React.SyntheticEvent | React.SyntheticEvent<Element, Event>) => {
    e.preventDefault();
    openChat();
  };

  const isChatLink = (link?: PrismicLink) => link?.url && link.url.includes('chat:open');

  const getPrimaryButton = (
    text?: TextFromRichText,
    link?: PrismicLink,
    onClick?: (e: React.SyntheticEvent | React.SyntheticEvent<Element, Event>) => void
  ): LayoutButtonProps | undefined => getButton(text, link, onClick) as LayoutButtonProps;

  const TextLayoutPrimary = component.primary;
  const LeftPrimaryButton: LayoutButtonProps | undefined = getPrimaryButton(
    TextLayoutPrimary.left_section_primary_button_text,
    TextLayoutPrimary.left_section_primary_button_link,
    isChatLink(TextLayoutPrimary.left_section_primary_button_link) ? onClickChat : undefined
  );
  const LeftSecondaryButton: LayoutButtonProps | undefined = getPrimaryButton(
    TextLayoutPrimary.left_section_secondary_button_text,
    TextLayoutPrimary.left_section_secondary_button_link,
    isChatLink(TextLayoutPrimary.left_section_secondary_button_link) ? onClickChat : undefined
  );
  const RightPrimaryButton: LayoutButtonProps | undefined = getPrimaryButton(
    TextLayoutPrimary.right_section_primary_button_text,
    TextLayoutPrimary.right_section_primary_button_link,
    isChatLink(TextLayoutPrimary.right_section_primary_button_link) ? onClickChat : undefined
  );
  const RightSecondaryButton: LayoutButtonProps | undefined = getPrimaryButton(
    TextLayoutPrimary.right_section_secondary_button_text,
    TextLayoutPrimary.right_section_secondary_button_link,
    isChatLink(TextLayoutPrimary.right_section_secondary_button_link) ? onClickChat : undefined
  );

  const TwoLayoutLeftSection = {
    title: TextLayoutPrimary.left_section_title?.text,
    content: parse(TextLayoutPrimary.left_section_content?.html || '', {
      replace: (domNode) => replaceElement(domNode, 'shade.800'),
    }),
    primaryButton: LeftPrimaryButton,
    secondaryButton: LeftSecondaryButton,
  };

  const OneLayoutLeftSection = {
    title: TextLayoutPrimary.text_layout_title.text,
  };

  const TwoLayoutRightSection = {
    title: TextLayoutPrimary.right_section_title?.text,
    content: parse(TextLayoutPrimary.right_section_content?.html || '', {
      replace: (domNode) => replaceElement(domNode, 'shade.800'),
    }),
    primaryButton: RightPrimaryButton,
    secondaryButton: RightSecondaryButton,
  };

  const OneLayoutRightSection = {
    content: parse(TextLayoutPrimary.text_layout_content?.html || '', {
      replace: (domNode) => replaceElement(domNode, 'shade.800'),
    }),
    primaryButton: {
      url: embedMarketingTrackingIntoURL(TextLayoutPrimary.text_layout_primary_button_link?.url || ''),
      text: TextLayoutPrimary.text_layout_primary_button_text?.text || '',
    },
    secondaryButton: {
      url: embedMarketingTrackingIntoURL(TextLayoutPrimary.text_layout_secondary_button_link?.url || ''),
      text: TextLayoutPrimary.text_layout_secondary_button_text?.text || '',
    },
  };

  return (
    <TextLayout
      variant={TextLayoutPrimary.variant}
      layoutVariant={layoutVariant}
      title={layoutVariant === 'two' ? TextLayoutPrimary.text_layout_title.text : ''}
      leftSection={layoutVariant === 'one' ? OneLayoutLeftSection : TwoLayoutLeftSection}
      rightSection={layoutVariant === 'one' ? OneLayoutRightSection : TwoLayoutRightSection}
    />
  );
};

export default PrismicTextLayout;
